import { useCallback, useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import { createRoute } from '@tanstack/react-router';
import { Bell, Crosshair, Database, Info, Map as MapIcon, RotateCcw, SlidersHorizontal, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { armConfirm } from '@/lib/armConfirm';
import { currentAppVersion } from '@/lib/updater';
import { loadJson } from '@/lib/persist';
import { SOURCE_META, useNotifications, type AlertSource } from '@/store/notifications';
import { recordEvent } from '@/store/appEvents';
import { useFleet } from '@/store/fleet';
import { useGotoTargets, distMeters } from '@/store/gotoTargets';
import { fetchOperatorPos, useOperatorPos } from '@/store/operatorPos';
import { useToasts } from '@/store/toasts';
import { useSettings, type TileSourceKind } from '@/store/settings';
import { buildStyle } from '@/map/buildStyle';
import { registerPmtilesProtocol } from '@/map/protocol';
import {
  deleteAllTrails,
  deleteOlderThan,
  enforceBudget,
  fmtBytes,
  trailStats,
  type TrailStats,
} from '@/lib/trailDb';
import { cn } from '@/lib/utils';
import { rootRoute } from './__root';

type Category = 'preferences' | 'map' | 'storage' | 'notifications' | 'about';

const CATEGORIES: { id: Category; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'preferences', label: 'Preferences', icon: SlidersHorizontal },
  { id: 'map', label: 'Map', icon: MapIcon },
  { id: 'storage', label: 'Storage', icon: Database },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'about', label: 'About', icon: Info },
];

const SOURCE_OPTIONS: { value: TileSourceKind; label: string }[] = [
  { value: 'openfreemap', label: 'OpenFreeMap — free, no key' },
  { value: 'maptiler', label: 'MapTiler — needs API key' },
  { value: 'mapbox', label: 'Mapbox — needs API key' },
  { value: 'pmtiles', label: 'Offline — PMTiles via Hub' },
  { value: 'custom', label: 'Custom style URL' },
];

/**
 * Settings — category nav left, document-style settings right (title +
 * description per row, control on the far right). Only categories whose
 * settings the app actually consumes exist; more return as features land.
 */
function SettingsWorkspace() {
  const [category, setCategory] = useState<Category>('map');

  return (
    <div className="flex h-full bg-base">
      {/* Category nav */}
      <aside className="w-56 flex-none border-r border-border-default bg-surface-1 px-3 py-5">
        <h1 className="px-2 text-[15px] font-semibold tracking-tight text-fg-primary">
          Settings
        </h1>
        <nav className="mt-4 space-y-0.5">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const active = category === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setCategory(c.id)}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded px-2 py-1.5 text-left text-[12.5px] transition-colors',
                  active
                    ? 'bg-surface-2 font-medium text-fg-primary'
                    : 'text-fg-secondary hover:bg-surface-2/60 hover:text-fg-primary',
                )}
              >
                <Icon className={cn('h-3.5 w-3.5', active ? 'text-accent' : 'text-fg-muted')} />
                {c.label}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="w-full px-8 py-7">
          {category === 'preferences' ? (
            <PreferencesPage />
          ) : category === 'map' ? (
            <MapSettingsPage />
          ) : category === 'storage' ? (
            <StorageSettingsPage />
          ) : category === 'notifications' ? (
            <NotificationsSettingsPage />
          ) : (
            <AboutPage />
          )}
        </div>
      </main>
    </div>
  );
}

/** GOTO safety radii (F025) — these live in the gotoTargets store (its own
 *  localStorage key), not map settings; the rows just surface them. */
function GotoSafetySection() {
  const settings = useGotoTargets((s) => s.settings);
  const setSettings = useGotoTargets((s) => s.setSettings);
  return (
    <>
      <SectionHeading
        title="GOTO safety"
        description="Guards on GOTO-mode commands. Distances in metres. 0 turns a guard off."
      />
      <Row
        title="Far-target confirm radius"
        description="Catches accidental clicks. If the flight leg would be longer than this, the first click only warns you. Click the same spot again within 8 seconds to actually send it."
        control={
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min={0}
              max={100000}
              step={100}
              value={settings.confirmRadiusM}
              onChange={(e) =>
                setSettings({ confirmRadiusM: Math.max(0, Math.round(Number(e.target.value) || 0)) })
              }
              className={cn(inputCls, 'w-24 text-center')}
            />
            <span className="text-xs text-fg-muted">m</span>
          </div>
        }
      />
      <Row
        title="Soft geofence radius"
        description="Marks your working area around the drone's home (the point RTL returns to). If a GOTO target is farther from home than this, its marker turns red and shows the distance, so you know the drone is being sent outside the area it can easily return from. It only warns. The command still goes through."
        control={
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min={0}
              max={100000}
              step={100}
              value={settings.fenceRadiusM}
              onChange={(e) =>
                setSettings({ fenceRadiusM: Math.max(0, Math.round(Number(e.target.value) || 0)) })
              }
              className={cn(inputCls, 'w-24 text-center')}
            />
            <span className="text-xs text-fg-muted">m</span>
          </div>
        }
      />
    </>
  );
}

/** "Pick on map" — opens a DIALOG map (operator ask; user-initiated setup
 *  dialogs are fine — it's async event modals that are banned) at the
 *  current default view showing assets, current home and the pilot's
 *  position; one click sets home. */
function HomePickRow() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <Row
        title="Pick on map"
        description="Open a map dialog at the current view and click where home should be. Assets, current home and your (pilot) position are shown."
        control={
          <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
            <MapIcon className="h-3.5 w-3.5" />
            Open map
          </Button>
        }
      />
      {open && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-6"
          onClick={() => setOpen(false)}
        >
          <div
            className="flex w-full max-w-[820px] flex-col overflow-hidden rounded-lg border border-border-default bg-surface-1 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-border-default px-4 py-2.5">
              <div>
                <div className="text-[13px] font-semibold text-fg-primary">Pick home position</div>
                <div className="text-[11px] text-fg-muted">
                  Click the map — <span className="text-amber-300">amber ring</span> = current home
                  · <span className="text-accent">cyan dots</span> = assets ·{' '}
                  <span className="text-accent">person</span> = pilot · Esc closes
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="grid h-7 w-7 place-items-center rounded text-fg-muted hover:bg-surface-2 hover:text-fg-primary"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <HomePickMap onPicked={() => setOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
}

function HomePickMap({ onPicked }: { onPicked: () => void }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const onPickedRef = useRef(onPicked);
  onPickedRef.current = onPicked;

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    registerPmtilesProtocol();
    const s = useSettings.getState().map;
    const m = new maplibregl.Map({
      container: node,
      style: buildStyle(s) as maplibregl.StyleSpecification | string,
      center: [s.initialLon, s.initialLat],
      zoom: s.initialZoom,
      attributionControl: false,
    });
    const markers: maplibregl.Marker[] = [];
    const HOUSE_SVG =
      '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#e8a83a" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>';
    const PERSON_SVG =
      '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#4fc8e0" stroke-width="2.4" stroke-linecap="round"><circle cx="12" cy="7" r="4"/><path d="M5.5 21a6.5 6.5 0 0 1 13 0"/></svg>';

    interface Landmark {
      lon: number;
      lat: number;
      color: string;
      label: string;
      svg: string;
    }
    const landmarks: Landmark[] = [
      { lon: s.initialLon, lat: s.initialLat, color: '#e8a83a', label: 'home', svg: HOUSE_SVG },
    ];
    const fleet = useFleet.getState();
    for (const [id, st] of Object.entries(fleet.states)) {
      if (!st?.position) continue;
      landmarks.push({
        lon: st.position.lon,
        lat: st.position.lat,
        color: '#4fc8e0',
        label: fleet.robots[id]?.name ?? id,
        svg: '',
      });
    }

    /** Co-located landmarks (≤ ~25 m) merge into ONE icon with a single
     *  "home; pilot" tag — stacked separate icons read as different places
     *  (operator report). */
    function render() {
      markers.forEach((mk) => mk.remove());
      markers.length = 0;
      const groups: Landmark[][] = [];
      for (const lm of landmarks) {
        const g = groups.find(
          (grp) => distMeters(grp[0]!, { lat: lm.lat, lon: lm.lon }) <= 25,
        );
        if (g) g.push(lm);
        else groups.push([lm]);
      }
      const bounds = new maplibregl.LngLatBounds(
        [landmarks[0]!.lon, landmarks[0]!.lat],
        [landmarks[0]!.lon, landmarks[0]!.lat],
      );
      for (const g of groups) {
        // Prefer the home ring's identity when it's in the group.
        const lead = g.find((x) => x.label === 'home') ?? g[0]!;
        const label = g.map((x) => x.label).join('; ');
        const el = document.createElement('div');
        el.style.cssText = 'position:relative;display:grid;place-items:center;z-index:10;';
        el.innerHTML =
          `<span style="display:grid;place-items:center;width:22px;height:22px;border-radius:50%;` +
          `background:rgba(14,15,17,0.88);border:2px solid ${lead.color};box-sizing:border-box;">${lead.svg}</span>` +
          `<span style="position:absolute;top:calc(100% + 2px);left:50%;transform:translateX(-50%);` +
          `white-space:nowrap;font-family:var(--font-mono);font-size:9px;letter-spacing:0.1em;` +
          `text-transform:uppercase;color:${lead.color};background:rgba(14,15,17,0.82);padding:1px 4px;` +
          `border-radius:2px;">${label}</span>`;
        el.title = label;
        markers.push(
          new maplibregl.Marker({ element: el, anchor: 'center' })
            .setLngLat([lead.lon, lead.lat])
            .addTo(m),
        );
        bounds.extend([lead.lon, lead.lat]);
      }
      // Frame EVERYTHING so nothing sits off-screen.
      if (groups.length > 1) m.fitBounds(bounds, { padding: 90, maxZoom: 15, duration: 0 });
    }
    render();

    // Pilot — known position, else ask the browser now so the dialog shows
    // it without a prior TopBar fetch. Re-renders the groups on arrival.
    const addPilot = (lat: number, lon: number) => {
      landmarks.push({ lon, lat, color: '#4fc8e0', label: 'pilot', svg: PERSON_SVG });
      render();
    };
    const pilot = useOperatorPos.getState().pos;
    if (pilot) addPilot(pilot.lat, pilot.lon);
    else if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          useOperatorPos.getState().set({ lat: pos.coords.latitude, lon: pos.coords.longitude });
          addPilot(pos.coords.latitude, pos.coords.longitude);
        },
        () => {},
        { enableHighAccuracy: true, timeout: 8_000 },
      );
    }
    m.on('click', (e) => {
      const lat = Number(e.lngLat.lat.toFixed(6));
      const lon = Number(e.lngLat.lng.toFixed(6));
      useSettings.getState().setMap({ initialLat: lat, initialLon: lon });
      recordEvent('settings', `home position → ${lat}, ${lon} (picked on map)`);
      useToasts.getState().push({
        tone: 'good',
        title: 'Home position set',
        body: `${lat}, ${lon}`,
        durationMs: 4000,
      });
      onPickedRef.current();
    });
    return () => {
      markers.forEach((mk) => mk.remove());
      m.remove();
    };
  }, []);

  return <div ref={ref} className="h-[55vh] w-full cursor-crosshair" />;
}

/* ================================================================== */
/* Preferences — app-wide, not tied to one workspace                   */
/* ================================================================== */

function PreferencesPage() {
  const prefs = useSettings((s) => s.prefs);
  const setPrefs = useSettings((s) => s.setPrefs);

  return (
    <>
      <PageHeader title="Preferences" />
      <SectionHeading
        title="Units"
        description="Applies everywhere: map labels, telemetry panel, measure readouts."
      />
      <Row
        title="Distance unit"
        description="Every distance readout in the app."
        control={
          <select
            value={prefs.distanceUnit}
            onChange={(e) => {
              setPrefs({ distanceUnit: e.target.value as 'm' | 'km' });
              recordEvent('settings', `distance unit → ${e.target.value}`);
            }}
            className={cn(inputCls, 'w-24')}
          >
            <option value="m">metres</option>
            <option value="km">kilometres</option>
          </select>
        }
      />
    </>
  );
}

/* ================================================================== */
/* Map settings                                                        */
/* ================================================================== */

function MapSettingsPage() {
  const map = useSettings((s) => s.map);
  const setMap = useSettings((s) => s.setMap);
  const reset = useSettings((s) => s.reset);

  // ("Copy from the live map" replaced 2026-07-14 by "Use pilot position
  // as home" — operator call.)

  return (
    <>
      <PageHeader
        title="Map"
        action={
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              if (armConfirm('settings-reset-map', 'Reset map settings to defaults?')) {
                reset();
                recordEvent('settings', 'map settings reset to defaults');
              }
            }}
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset to defaults
          </Button>
        }
      />

      <SectionHeading
        title="Basemap"
        description="Where map tiles come from. Changes apply live to every map in the app."
      />
      <Row
        title="Tile source"
        description="OpenFreeMap works out of the box. Offline uses a PMTiles region file served by the Hub."
        control={
          <select
            value={map.source}
            onChange={(e) => {
              setMap({ source: e.target.value as TileSourceKind });
              recordEvent('settings', `tile source → ${e.target.value}`);
            }}
            className={cn(inputCls, 'w-64')}
          >
            {SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        }
      />
      {(map.source === 'maptiler' || map.source === 'mapbox') && (
        <Row
          title="API key"
          description={`Your ${map.source === 'maptiler' ? 'MapTiler' : 'Mapbox'} key. Stored locally in this browser.`}
          control={
            <input
              value={map.apiKey}
              onChange={(e) => setMap({ apiKey: e.target.value })}
              placeholder="paste key"
              className={cn(inputCls, 'w-64')}
            />
          }
        />
      )}
      {map.source === 'custom' && (
        <Row
          title="Style URL"
          description="A full MapLibre style.json URL."
          control={
            <input
              value={map.styleUrl}
              onChange={(e) => setMap({ styleUrl: e.target.value })}
              placeholder="https://…/style.json"
              className={cn(inputCls, 'w-64')}
            />
          }
        />
      )}
      {map.source === 'pmtiles' && (
        <Row
          title="PMTiles URL"
          description="Served by the Hub. Drop a region file in its tiles directory."
          control={
            <input
              value={map.pmtilesUrl}
              onChange={(e) => setMap({ pmtilesUrl: e.target.value })}
              placeholder="/tiles/region.pmtiles"
              className={cn(inputCls, 'w-64')}
            />
          }
        />
      )}

      <Row
        title="Cache tiles on Hub"
        description="Basemap requests go through the Hub, which keeps every tile on disk. Repeat loads are instant and already-seen regions work offline."
        control={
          <Toggle
            checked={map.cacheTiles !== false}
            onChange={(v) => {
              setMap({ cacheTiles: v });
              recordEvent('settings', `hub tile cache ${v ? 'enabled' : 'disabled'}`);
            }}
          />
        }
      />

      <Row
        title="Switch to Measure mode"
        description="Seconds of GOTO-mode inactivity (no clicks, pans or cursor movement over the map) before it switches back to Measure, so a stray click can't send an unintended GOTO. 0 disables."
        control={
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min={0}
              max={600}
              step={15}
              value={map.directIdleExitS ?? 60}
              onChange={(e) =>
                setMap({
                  directIdleExitS: Math.max(0, Math.min(600, Math.round(Number(e.target.value) || 0))),
                })
              }
              className={cn(inputCls, 'w-20 text-center')}
            />
            <span className="text-xs text-fg-muted">s</span>
          </div>
        }
      />
      <Row
        title="Switch countdown warning"
        description="Show the '→ Measure in N s' chip on the mode bar when this many seconds remain."
        control={
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min={0}
              max={600}
              step={5}
              value={map.directIdleWarnS ?? 30}
              onChange={(e) =>
                setMap({
                  directIdleWarnS: Math.max(0, Math.min(600, Math.round(Number(e.target.value) || 0))),
                })
              }
              className={cn(inputCls, 'w-20 text-center')}
            />
            <span className="text-xs text-fg-muted">s</span>
          </div>
        }
      />

      <SectionHeading
        title="Default view"
        description="Where maps open in a fresh browser, before any camera position has been remembered."
      />
      <Row
        title="Home position"
        description="Default map view, and later the fleet headquarters. This is NOT the drones' RTL point. RTL defaults to each takeoff spot; override it per asset from its sidebar row."
        control={
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              step="0.0001"
              value={map.initialLat}
              onChange={(e) => setMap({ initialLat: Number(e.target.value) })}
              title="Latitude"
              className={cn(inputCls, 'w-28')}
            />
            <input
              type="number"
              step="0.0001"
              value={map.initialLon}
              onChange={(e) => setMap({ initialLon: Number(e.target.value) })}
              title="Longitude"
              className={cn(inputCls, 'w-28')}
            />
            <input
              type="number"
              min={1}
              max={20}
              step={0.5}
              value={map.initialZoom}
              onChange={(e) => setMap({ initialZoom: Number(e.target.value) })}
              title="Zoom"
              className={cn(inputCls, 'w-16 text-center')}
            />
          </div>
        }
      />
      <Row
        title="Use pilot position as home"
        description="Your browser geolocation becomes the home position. Once applies it now. Always keeps home following every position update."
        control={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                fetchOperatorPos();
                // One-shot even when Always is off: apply directly.
                if ('geolocation' in navigator) {
                  navigator.geolocation.getCurrentPosition(
                    (p) => {
                      setMap({
                        initialLat: Number(p.coords.latitude.toFixed(6)),
                        initialLon: Number(p.coords.longitude.toFixed(6)),
                      });
                      recordEvent('settings', 'home ← pilot position (once)');
                    },
                    () => {},
                    { enableHighAccuracy: true, timeout: 10_000 },
                  );
                }
              }}
            >
              <Crosshair className="h-3.5 w-3.5" />
              Once
            </Button>
            <span className="text-[11px] text-fg-muted">always</span>
            <Toggle
              checked={map.homeFollowsPilot === true}
              onChange={(v) => {
                setMap({ homeFollowsPilot: v });
                if (v) fetchOperatorPos();
                recordEvent('settings', `home follows pilot ${v ? 'enabled' : 'disabled'}`);
              }}
            />
          </div>
        }
      />
      <HomePickRow />

      <SectionHeading
        title="Assets"
        description="How asset overlays (GOTO leg labels) behave on the map."
      />
      <Row
        title="Merge leg labels below zoom"
        description="Zoomed out past this, per-leg distance labels collapse into one Σ-total per asset."
        control={
          <input
            type="number"
            min={0}
            max={22}
            step={0.5}
            value={map.legLabelMergeZoom ?? 13}
            onChange={(e) =>
              setMap({ legLabelMergeZoom: Math.max(0, Math.min(22, Number(e.target.value) || 0)) })
            }
            className={cn(inputCls, 'w-20 text-center')}
          />
        }
      />
      <Row
        title="Hide leg labels below zoom"
        description="Zoomed out past this, no leg labels render at all."
        control={
          <input
            type="number"
            min={0}
            max={22}
            step={0.5}
            value={map.legLabelHideZoom ?? 11}
            onChange={(e) =>
              setMap({ legLabelHideZoom: Math.max(0, Math.min(22, Number(e.target.value) || 0)) })
            }
            className={cn(inputCls, 'w-20 text-center')}
          />
        }
      />

      <GotoSafetySection />

      <SectionHeading
        title="3D terrain"
        description="Renders elevation from a DEM PMTiles file. Leave off unless you have one."
      />
      <Row
        title="Terrain"
        description="Drape the basemap over elevation data."
        control={
          <Toggle
            checked={map.terrain}
            onChange={(v) => {
              setMap({ terrain: v });
              recordEvent('settings', `3d terrain ${v ? 'enabled' : 'disabled'}`);
            }}
          />
        }
      />
      {map.terrain && (
        <>
          <Row
            title="DEM URL"
            description="Elevation tiles (PMTiles), e.g. served by the Hub."
            control={
              <input
                value={map.demUrl}
                onChange={(e) => setMap({ demUrl: e.target.value })}
                placeholder="/tiles/dem.pmtiles"
                className={cn(inputCls, 'w-64')}
              />
            }
          />
          <Row
            title="Exaggeration"
            description="Vertical scale multiplier (1–3)."
            control={
              <input
                type="number"
                min={1}
                max={3}
                step={0.1}
                value={map.terrainExaggeration}
                onChange={(e) => setMap({ terrainExaggeration: Number(e.target.value) })}
                className={cn(inputCls, 'w-20 text-center')}
              />
            }
          />
        </>
      )}
    </>
  );
}

/* ================================================================== */
/* Storage                                                             */
/* ================================================================== */

function StorageSettingsPage() {
  const budgetMb = useSettings((s) => s.storage.trailBudgetMb);
  const setStorage = useSettings((s) => s.setStorage);
  const [stats, setStats] = useState<TrailStats | null>(null);
  const [busy, setBusy] = useState(false);
  const [customDays, setCustomDays] = useState(7);

  const refresh = useCallback(async () => {
    try {
      setStats(await trailStats());
    } catch {
      setStats(null);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function purge(days: number | 'all') {
    setBusy(true);
    try {
      if (days === 'all') {
        await deleteAllTrails();
        recordEvent('settings', 'trail history: deleted everything');
      } else {
        const n = await deleteOlderThan(days);
        recordEvent('settings', `trail history: deleted ${n} points older than ${days} d`);
      }
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  const pct =
    stats && budgetMb > 0
      ? Math.min(100, (stats.approxBytes / (budgetMb * 1024 * 1024)) * 100)
      : 0;

  return (
    <>
      <PageHeader title="Storage" />
      <SectionHeading
        title="Trail history"
        description="Flight breadcrumbs persist locally (IndexedDB) so a page refresh doesn't lose them. The last 24 hours restore onto the map at startup."
      />
      <Row
        title="Space used"
        description={
          stats
            ? `${stats.records.toLocaleString()} points recorded (approximate size — records are fixed-shape).`
            : 'Reading…'
        }
        control={
          <div className="w-44">
            <div className="text-right font-mono text-[12px] text-fg-primary">
              {stats ? fmtBytes(stats.approxBytes) : '—'}
              <span className="text-fg-muted"> / {fmtBytes(budgetMb * 1024 * 1024)}</span>
            </div>
            <div className="mt-1 h-1 overflow-hidden rounded bg-surface-2">
              <div
                className={cn('h-full', pct > 90 ? 'bg-status-warn' : 'bg-accent')}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        }
      />
      <Row
        title="Space budget"
        description="Hard cap. Once trail history exceeds it, the oldest points are pruned automatically after each write."
        control={
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min={16}
              max={10240}
              step={64}
              value={budgetMb}
              onChange={(e) => {
                const v = Math.min(10240, Math.max(16, Number(e.target.value) || 16));
                setStorage({ trailBudgetMb: v });
              }}
              onBlur={() => {
                void enforceBudget(useSettings.getState().storage.trailBudgetMb).then(() =>
                  refresh(),
                );
              }}
              className={cn(inputCls, 'w-24 text-center')}
            />
            <span className="text-[11px] text-fg-muted">MB</span>
          </div>
        }
      />
      <Row
        title="Browser origin usage"
        description="Everything this site stores (tiles cache, settings, trail history) vs what the browser allows."
        control={
          <span className="font-mono text-[12px] text-fg-secondary">
            {stats?.originUsageBytes != null ? fmtBytes(stats.originUsageBytes) : '—'}
            {stats?.originQuotaBytes != null && (
              <span className="text-fg-muted"> / {fmtBytes(stats.originQuotaBytes)}</span>
            )}
          </span>
        }
      />

      <SectionHeading
        title="Delete trail history"
        description="Remove recorded breadcrumbs by age. This only affects this browser's copy, never the Hub."
      />
      <Row
        title="Older than…"
        description="Keep recent flights, drop the archive tail."
        control={
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => void purge(1)}>
              1 day
            </Button>
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => void purge(7)}>
              7 days
            </Button>
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => void purge(30)}>
              30 days
            </Button>
          </div>
        }
      />
      <Row
        title="Custom age"
        description="Delete everything older than this many days."
        control={
          <div className="flex items-center gap-1.5">
            <input
              type="number"
              min={0}
              max={365}
              value={customDays}
              onChange={(e) => setCustomDays(Math.max(0, Number(e.target.value) || 0))}
              className={cn(inputCls, 'w-20 text-center')}
            />
            <span className="text-[11px] text-fg-muted">days</span>
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => void purge(customDays)}>
              Delete
            </Button>
          </div>
        }
      />
      <Row
        title="Everything"
        description="Wipe all recorded trail history from this browser."
        control={
          <Button
            size="sm"
            variant="secondary"
            disabled={busy}
            onClick={() => {
              if (armConfirm('purge-trails-all', 'Delete ALL locally stored trail history?')) void purge('all');
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete all
          </Button>
        }
      />
    </>
  );
}

/* ================================================================== */
/* Notifications                                                       */
/* ================================================================== */

function NotificationsSettingsPage() {
  const audio = useNotifications((s) => s.audio);
  const desktopPush = useNotifications((s) => s.desktopPush);
  const toggleAudio = useNotifications((s) => s.toggleAudio);
  const toggleDesktopPush = useNotifications((s) => s.toggleDesktopPush);
  const muted = useNotifications((s) => s.muted);
  const unmute = useNotifications((s) => s.unmute);

  const activeMutes = (Object.entries(muted) as [AlertSource, number][]).filter(
    ([, expiry]) => expiry > Date.now(),
  );

  return (
    <>
      <PageHeader title="Notifications" />
      <SectionHeading
        title="Delivery"
        description="How alerts reach you when they arrive. The bell in the top bar always shows them."
      />
      <Row
        title="Audio alerts"
        description="Play a sound when a critical alert arrives."
        control={<Toggle checked={audio} onChange={toggleAudio} />}
      />
      <Row
        title="Desktop push"
        description="Also raise an operating-system notification."
        control={<Toggle checked={desktopPush} onChange={toggleDesktopPush} />}
      />

      <SectionHeading
        title="Muted sources"
        description="Alert sources you've temporarily silenced from the critical-alert flash."
      />
      {activeMutes.length === 0 ? (
        <p className="py-3 text-[12px] text-fg-muted">Nothing is muted.</p>
      ) : (
        activeMutes.map(([source, expiry]) => (
          <Row
            key={source}
            title={SOURCE_META[source].label}
            description={`Muted until ${new Date(expiry).toLocaleTimeString([], { hour12: false })}.`}
            control={
              <Button size="sm" variant="secondary" onClick={() => unmute(source)}>
                <X className="h-3.5 w-3.5" />
                Unmute
              </Button>
            }
          />
        ))
      )}
    </>
  );
}

/* ================================================================== */
/* About                                                               */
/* ================================================================== */

function AboutPage() {
  return (
    <>
      <PageHeader title="About" />
      <SectionHeading title="Console" description="Build and runtime information." />
      <Row title="Version" description="Console web client." control={
        <span className="font-mono text-[12px] text-fg-primary">v{currentAppVersion()}</span>
      } />
      <Row
        title="More settings"
        description="Display, notifications and connection preferences return here as those features land."
        control={<span className="text-[11px] text-fg-muted">coming back</span>}
      />
    </>
  );
}

/* ================================================================== */
/* Primitives                                                          */
/* ================================================================== */

function PageHeader({ title, action }: { title: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-border-default pb-3">
      <h2 className="text-[19px] font-semibold tracking-tight text-fg-primary">{title}</h2>
      {action}
    </div>
  );
}

function SectionHeading({ title, description }: { title: string; description: string }) {
  // Accent bar + rule so sections read at a glance inside long pages
  // (operator: "hard to see section inside map").
  return (
    <div className="mt-9 mb-2 border-b border-border-default pb-2">
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className="h-3.5 w-[3px] rounded-full"
          style={{ background: 'var(--color-accent)' }}
        />
        <h3 className="text-[13px] font-semibold uppercase tracking-[0.08em] text-fg-primary">
          {title}
        </h3>
      </div>
      <p className="mt-1 text-[12px] leading-relaxed text-fg-muted">{description}</p>
    </div>
  );
}

function Row({
  title,
  description,
  control,
}: {
  title: string;
  description: string;
  control: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-6 border-b border-border-subtle py-3.5 last:border-b-0">
      <div className="min-w-0">
        <div className="text-[13px] font-medium text-fg-primary">{title}</div>
        <div className="mt-0.5 max-w-[480px] text-[11.5px] leading-relaxed text-fg-muted">
          {description}
        </div>
      </div>
      <div className="flex-none">{control}</div>
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative h-5 w-9 flex-none rounded-full border transition-colors',
        checked ? 'border-accent bg-accent/40' : 'border-border-default bg-surface-2',
      )}
    >
      <span
        className={cn(
          'absolute top-1/2 h-3.5 w-3.5 -translate-y-1/2 rounded-full transition-[left]',
          checked ? 'left-[18px] bg-accent' : 'left-[3px] bg-fg-muted',
        )}
      />
    </button>
  );
}

const inputCls =
  'h-8 rounded border border-border-default bg-surface-1 px-2.5 font-mono text-[12px] text-fg-primary placeholder:text-fg-disabled outline-none focus:border-accent';

export const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsWorkspace,
});
